import React, { useState, useEffect } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useLanguage } from '../i18n/LanguageContext';
import { getServiceById, Service, ServiceProvider } from '../utils/servicesAPI';
import ServiceRequestForm from '../components/ui/ServiceRequestForm';
import Button from '../components/ui/Button';
import Spinner from '../components/ui/Spinner';
import { Helmet } from 'react-helmet';

// Styled components
const PageContainer = styled.div`
  background-color: #000;
  color: #fff;
  min-height: 100vh;
  padding-top: 70px;
`;

const ContentContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 3rem 1rem;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: transparent;
  border: 1px solid #333;
  color: #0d9488;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  cursor: pointer;
  margin-bottom: 2rem;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: #0d9488;
    background: rgba(13, 148, 136, 0.1);
  }
`;

const ServiceHeader = styled.div`
  display: grid;
  grid-template-columns: 1fr 400px;
  gap: 3rem;
  margin-bottom: 3rem;
  
  @media (max-width: 992px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const ServiceImageContainer = styled.div`
  position: relative;
  height: 400px;
  border-radius: 12px;
  overflow: hidden;
`;

const ServiceImage = styled.div<{ $image?: string }>`
  width: 100%;
  height: 100%;
  background: ${props => props.$image ? 
    `linear-gradient(rgba(0,0,0,0.3), rgba(0,0,0,0.3)), url(${props.$image})` : 
    'linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)'
  };
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ServiceIcon = styled.div`
  width: 100px;
  height: 100px;
  background: rgba(13, 148, 136, 0.8);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 3rem;
`;

const ServiceDetails = styled.div`
  display: flex;
  flex-direction: column;
`;

const ServiceCategory = styled.span`
  display: inline-block;
  background: #0d9488;
  color: #fff;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 500;
  text-transform: uppercase;
  margin-bottom: 1rem;
  align-self: flex-start;
`;

const ServiceTitle = styled.h1`
  font-size: 2.5rem;
  margin-bottom: 1rem;
  color: #fff;
  line-height: 1.2;
`;

const ServiceDescription = styled.p`
  color: #ccc;
  font-size: 1.1rem;
  line-height: 1.6;
  margin-bottom: 2rem;
`;

const ServiceMetaGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  margin-bottom: 2rem;
  
  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

const ServiceMetaItem = styled.div`
  background: rgba(13, 148, 136, 0.1);
  border: 1px solid #0d9488;
  border-radius: 8px;
  padding: 1rem;
`;

const MetaLabel = styled.div`
  color: #0d9488;
  font-size: 0.9rem;
  font-weight: 500;
  margin-bottom: 0.25rem;
`;

const MetaValue = styled.div`
  color: #fff;
  font-size: 1.1rem;
  font-weight: 600;
`;

const ProviderSection = styled.div`
  background: linear-gradient(135deg, #111 0%, #1a1a1a 100%);
  border: 1px solid #333;
  border-radius: 12px;
  padding: 2rem;
  margin-bottom: 3rem;
`;

const ProviderHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const ProviderAvatar = styled.div`
  width: 60px;
  height: 60px;
  background: #0d9488;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: #fff;
`;

const ProviderInfo = styled.div`
  flex: 1;
`;

const ProviderName = styled.h3`
  font-size: 1.3rem;
  color: #0d9488;
  margin-bottom: 0.25rem;
`;

const ProviderSpecialization = styled.div`
  color: #ccc;
  font-size: 0.95rem;
`;

const ProviderBio = styled.p`
  color: #ccc;
  line-height: 1.5;
  margin-bottom: 1rem;
`;

const ContactInfo = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  
  @media (max-width: 576px) {
    flex-direction: column;
  }
`;

const ContactButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 500;
  transition: all 0.3s ease;
  
  &.email {
    background: rgba(59, 130, 246, 0.1);
    border: 1px solid #3b82f6;
    color: #3b82f6;
    
    &:hover {
      background: #3b82f6;
      color: #fff;
    }
  }
  
  &.whatsapp {
    background: rgba(37, 211, 102, 0.1);
    border: 1px solid #25d366;
    color: #25d366;
    
    &:hover {
      background: #25d366;
      color: #fff;
    }
  }
  
  &.phone {
    background: rgba(13, 148, 136, 0.1);
    border: 1px solid #0d9488;
    color: #0d9488;
    
    &:hover {
      background: #0d9488;
      color: #fff;
    }
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 50vh;
  flex-direction: column;
  gap: 1rem;
`;

const ErrorContainer = styled.div`
  text-align: center;
  padding: 3rem;
  color: #ef4444;
`;

const getCategoryIcon = (category: string): string => {
  switch (category.toLowerCase()) {
    case 'wellness':
      return '🧘';
    case 'catering':
      return '🍽️';
    case 'courses':
      return '📚';
    case 'events':
      return '🎉';
    case 'fitness':
      return '💪';
    case 'culture':
      return '🎭';
    default:
      return '⭐';
  }
};

const ServiceRequest: React.FC = () => {
  const { serviceId } = useParams<{ serviceId: string }>();
  const { language } = useLanguage();
  const [service, setService] = useState<Service | null>(null);
  const [provider, setProvider] = useState<ServiceProvider | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (serviceId) {
      loadService(serviceId);
    }
  }, [serviceId]);

  const loadService = async (id: string) => {
    setLoading(true);
    try {
      const result = await getServiceById(id);
      if (result.success && result.service) {
        setService(result.service);
        setProvider(result.service.provider as ServiceProvider);
        setError(null);
      } else {
        setError(result.error || 'Servizio non trovato');
      }
    } catch (err) {
      setError('Errore durante il caricamento del servizio');
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (minutes?: number): string => {
    if (!minutes) return language === 'it' ? 'Non specificata' : 'Not specified';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    
    if (hours === 0) return `${mins} min`;
    if (mins === 0) return `${hours}h`;
    return `${hours}h ${mins}min`;
  };

  const formatPrice = (): string => {
    if (!service) return '';
    
    if (service.price_per_person) {
      return `€${service.price_per_person}/${language === 'it' ? 'persona' : 'person'}`;
    }
    if (service.price_per_group) {
      return `€${service.price_per_group}/${language === 'it' ? 'gruppo' : 'group'}`;
    }
    return language === 'it' ? 'Su richiesta' : 'On request';
  };

  const getCategoryLabel = (category: string): string => {
    const labels: { [key: string]: { it: string; en: string } } = {
      wellness: { it: 'Benessere', en: 'Wellness' },
      catering: { it: 'Catering', en: 'Catering' },
      courses: { it: 'Corsi', en: 'Courses' },
      events: { it: 'Eventi', en: 'Events' },
      fitness: { it: 'Fitness', en: 'Fitness' },
      culture: { it: 'Cultura', en: 'Culture' }
    };
    
    return labels[category] ? 
      (language === 'it' ? labels[category].it : labels[category].en) : 
      category;
  };

  // Redirect se non c'è serviceId
  if (!serviceId) {
    return <Navigate to="/services/catalog" replace />;
  }

  // Loading state
  if (loading) {
    return (
      <PageContainer>
        <ContentContainer>
          <LoadingContainer>
            <Spinner />
            <p>{language === 'it' ? 'Caricamento servizio...' : 'Loading service...'}</p>
          </LoadingContainer>
        </ContentContainer>
      </PageContainer>
    );
  }

  // Error state
  if (error || !service || !provider) {
    return (
      <PageContainer>
        <ContentContainer>
          <ErrorContainer>
            <h2>{language === 'it' ? 'Servizio non trovato' : 'Service not found'}</h2>
            <p>{error}</p>
            <Button 
              variant="primary" 
              onClick={() => window.history.back()}
              style={{ marginTop: '1rem' }}
            >
              {language === 'it' ? '← Torna indietro' : '← Go back'}
            </Button>
          </ErrorContainer>
        </ContentContainer>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Helmet>
        <title>
          {(language === 'it' ? service.title_it : service.title_en) + ' | J-O-H'}
        </title>
        <meta 
          name="description" 
          content={language === 'it' ? service.description_it : service.description_en} 
        />
      </Helmet>

      <ContentContainer>
        <BackButton onClick={() => window.history.back()}>
          ← {language === 'it' ? 'Torna al catalogo' : 'Back to catalog'}
        </BackButton>

        <ServiceHeader>
          <div>
            <ServiceImageContainer>
              <ServiceImage $image={service.images?.[0]}>
                <ServiceIcon>
                  {getCategoryIcon(service.category)}
                </ServiceIcon>
              </ServiceImage>
            </ServiceImageContainer>
          </div>

          <ServiceDetails>
            <ServiceCategory>
              {getCategoryLabel(service.category)}
            </ServiceCategory>
            
            <ServiceTitle>
              {language === 'it' ? service.title_it : service.title_en}
            </ServiceTitle>
            
            <ServiceDescription>
              {language === 'it' ? service.description_it : service.description_en}
            </ServiceDescription>

            <ServiceMetaGrid>
              <ServiceMetaItem>
                <MetaLabel>
                  {language === 'it' ? 'Durata' : 'Duration'}
                </MetaLabel>
                <MetaValue>
                  {formatDuration(service.duration_minutes)}
                </MetaValue>
              </ServiceMetaItem>

              <ServiceMetaItem>
                <MetaLabel>
                  {language === 'it' ? 'Max partecipanti' : 'Max participants'}
                </MetaLabel>
                <MetaValue>
                  {service.max_participants}
                </MetaValue>
              </ServiceMetaItem>

              <ServiceMetaItem>
                <MetaLabel>
                  {language === 'it' ? 'Prezzo' : 'Price'}
                </MetaLabel>
                <MetaValue>
                  {formatPrice()}
                </MetaValue>
              </ServiceMetaItem>

              {service.location && (
                <ServiceMetaItem>
                  <MetaLabel>
                    {language === 'it' ? 'Luogo' : 'Location'}
                  </MetaLabel>
                  <MetaValue>
                    {service.location}
                  </MetaValue>
                </ServiceMetaItem>
              )}
            </ServiceMetaGrid>
          </ServiceDetails>
        </ServiceHeader>

        {/* Provider Info */}
        <ProviderSection>
          <ProviderHeader>
            <ProviderAvatar>
              {provider.profile_image ? (
                <img 
                  src={provider.profile_image} 
                  alt={provider.name}
                  style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }}
                />
              ) : (
                provider.name.charAt(0)
              )}
            </ProviderAvatar>
            <ProviderInfo>
              <ProviderName>{provider.name}</ProviderName>
              <ProviderSpecialization>
                {provider.specialization?.join(', ')}
                {provider.hourly_rate && ` • €${provider.hourly_rate}/h`}
              </ProviderSpecialization>
            </ProviderInfo>
          </ProviderHeader>

          {(provider.bio_it || provider.bio_en) && (
            <ProviderBio>
              {language === 'it' ? provider.bio_it : provider.bio_en}
            </ProviderBio>
          )}

          <ContactInfo>
            <ContactButton 
              href={`mailto:${provider.email}`}
              className="email"
            >
              ✉️ {language === 'it' ? 'Email' : 'Email'}
            </ContactButton>

            {provider.phone && (
              <ContactButton 
                href={`tel:${provider.phone}`}
                className="phone"
              >
                📞 {language === 'it' ? 'Telefono' : 'Phone'}
              </ContactButton>
            )}

            {provider.whatsapp && (
              <ContactButton 
                href={`https://wa.me/${provider.whatsapp.replace('+', '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="whatsapp"
              >
                📱 WhatsApp
              </ContactButton>
            )}
          </ContactInfo>
        </ProviderSection>

        {/* Service Requirements */}
        {service.requirements && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <ProviderSection>
              <h3 style={{ color: '#0d9488', marginBottom: '1rem' }}>
                {language === 'it' ? 'Requisiti e Note' : 'Requirements and Notes'}
              </h3>
              <p style={{ color: '#ccc', lineHeight: 1.6 }}>
                {service.requirements}
              </p>
            </ProviderSection>
          </motion.div>
        )}

        {/* Request Form */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <ServiceRequestForm 
            service={service}
            provider={provider}
          />
        </motion.div>
      </ContentContainer>
    </PageContainer>
  );
};

export default ServiceRequest; 